import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { StoryLabels } from '@org/ui';

// Every string of the story viewer, from `story.labels.*` in p2e.json.
// The `{name}`-style placeholders use single braces on purpose: i18next leaves
// them alone and the viewer fills them in.
const STORY_LABEL_KEYS = [
  'storyOf',
  'pause',
  'resume',
  'close',
  'paused',
  'rereading',
  'prevPage',
  'nextPage',
  'keysHint',
  'announce',
  'unitN',
  'revealKicker',
  'revealKickerNoUnit',
  'hintBefore',
  'hintAfter',
  'notebookCaption',
  'promise',
  'promiseFrom',
  'videoPlay',
  'videoReplay',
  'videoSkip',
  'videoEmpty',
  'videoFrameTitle',
  'closingKicker',
  'onWall',
  'firstClass',
  'startClass',
  'seeWall',
  'rereadHint',
  'cardText',
  'cardVideo',
  'cardNotebook',
  'cardQuote',
] as const satisfies readonly (keyof StoryLabels)[];

// Compile-time check that no StoryLabels key is missing from the list above.
type MissingStoryLabelKey = Exclude<keyof StoryLabels, (typeof STORY_LABEL_KEYS)[number]>;
const _allStoryLabelKeysListed: MissingStoryLabelKey extends never ? true : never = true;
void _allStoryLabelKeysListed;

/** The story viewer's strings in the app's current language. */
export function useStoryLabels(): StoryLabels {
  const { t, i18n } = useTranslation('p2e');
  return useMemo(() => {
    const labels = {} as StoryLabels;
    for (const key of STORY_LABEL_KEYS) {
      labels[key] = t(`story.labels.${key}`);
    }
    return labels;
    // i18n.language: rebuild when the student switches language in the side menu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, i18n.language]);
}
